import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { AuthContext } from "../../components/AuthContext";
import useDraftManager from '../../hooks/useDraftManager';
import { 
  FaEye, 
  FaTimes, 
  FaImage, 
  FaUpload, 
  FaTrash,
  FaCheckCircle,
  FaExclamationCircle,
  FaBox,
  FaRuler,
  FaTags,
  FaCube
} from 'react-icons/fa';
import Select from 'react-select';

export default function TilesUI() {
  const { user } = useContext(AuthContext);
  const [tilesName, setTilesName] = useState("");
  const [tilesMM, setTilesMM] = useState("");
  const [tilesInches, setTilesInches] = useState("");
  const [tilesCategory, setTilesCategory] = useState("");
  const [tilesType, setTilesType] = useState("");
  const [tilesFinishType, setTilesFinishType] = useState("");
  const [tilesPiecesPerBox, setTilesPiecesPerBox] = useState(0);
  const [tilesSqrFtPerBox, setTilesSqrFtPerBox] = useState(0);
  const [openingStock, setOpeningStock] = useState(0);
  const [tilesImage, setTilesImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [parameters, setParameters] = useState({
    mm: [],
    inches: [],
    category: [],
    type: [],
    finish: []
  });

  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [locations, setLocations] = useState([]);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Dark mode sync with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [form, setForm] = useState({
    tilesName: '',
    partNumber: '',
    displayName: '',
    tilesMM: '',
    tilesInches: '',
    tilesCategory: '',
    tilesType: '',
    tilesFinishType: '',
    tilesPiecesPerBox: '',
    tilesSqrFtPerBox: '',
    tilesImage: null,
    stock: locations.map(location => ({
      location: location._id,
      freshStockBox: 0,
      freshStockPieces: 0,
      damagedStockBox: 0,
      damagedStockPieces: 0
    }))
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Notification states for draft auto-save
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Helper function to prepare form data for draft saving (convert File to data URL)
  const prepareFormDataForDraft = async (formData) => {
    const preparedData = { ...formData };
    
    // Convert image File to data URL if it exists
    if (formData.tilesImage && formData.tilesImage instanceof File) {
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(formData.tilesImage);
        });
        preparedData.tilesImage = dataUrl;
      } catch (error) {
        console.error('Error converting image to data URL:', error);
        // If conversion fails, exclude the image from draft
        preparedData.tilesImage = null;
      }
    }
    
    return preparedData;
  };

  // Draft Manager for Tiles Management
  const draftManager = useDraftManager({
    featureType: 'tilesManagement',
    formData: form,
    setFormData: setForm,
    userId: user?.user?._id,
    companyId: user?.user?.company,
    prepareFormData: prepareFormDataForDraft,
    onDraftLoaded: (draft) => {
      console.log('Tiles draft loaded:', draft);
      Swal.fire({
        icon: 'success',
        title: 'Draft Loaded',
        text: 'Your previous draft has been loaded',
        timer: 2000,
        showConfirmButton: false
      });
    },
    onDraftSaved: (draft, isManual) => {
      setNotificationMessage('Draft saved automatically');
      setShowNotification(true);
    },
    onError: (error) => {
      console.error('Tiles draft error:', error);
    }
  });

  const convertPiecesToBoxes = (pieces, piecesPerBox) => {
    if (!piecesPerBox || piecesPerBox <= 0) return { boxes: 0, remainingPieces: pieces };
    const boxes = Math.floor(pieces / piecesPerBox);
    const remainingPieces = pieces % piecesPerBox;
    return { boxes, remainingPieces };
  };

  useEffect(() => {
    const fetchParameters = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tiles/parameters/all?userId=${user.user._id}`);
        const allParams = response.data;

        const groupedParams = {
          mm: allParams.filter(param => param.assignedFor === 'mm'),
          inches: allParams.filter(param => param.assignedFor === 'inches'),
          category: allParams.filter(param => param.assignedFor === 'category'),
          type: allParams.filter(param => param.assignedFor === 'type'),
          finish: allParams.filter(param => param.assignedFor === 'finish')
        };

        setParameters(groupedParams);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching parameters:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load parameters. Please try again.',
          showConfirmButton: true,
          confirmButtonText: 'Retry',
        }).then((result) => {
          if (result.isConfirmed) {
            fetchParameters();
          }
        });
      }
    };

    fetchParameters();
  }, []);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      // Try the /all endpoint first
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/brands/all`
      );
      
      if (response.data && response.data.success) {
        setBrands(response.data.brands);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch brands. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${user.user.company._id}`
      );
      setLocations(response.data);
      
      // Initialize stock array with all locations
      const initialStock = response.data.map(location => ({
        location: location._id,
        freshStockBox: 0,
        freshStockPieces: 0,
        damagedStockBox: 0,
        damagedStockPieces: 0
      }));
      
      setForm(prev => ({
        ...prev,
        stock: initialStock
      }));
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire('Error', 'Failed to fetch locations', 'error');
    }
  };

  const validateForm = () => {
    const errors = {};
    const upperName = form.tilesName.trim().toUpperCase();

    if (!upperName) {
      errors.tilesName = 'Tiles Name is required';
    }

    if (!form.tilesMM) errors.tilesMM = 'Tiles Size (MM) is required';
    if (!form.tilesInches) errors.tilesInches = 'Tiles Size (Inches) is required';
    if (!form.tilesCategory) errors.tilesCategory = 'Tiles Category is required';
    if (!form.tilesType) errors.tilesType = 'Tiles Type is required';
    if (!form.tilesFinishType) errors.tilesFinishType = 'Tiles Finish Type is required';
    if (!form.tilesPiecesPerBox || form.tilesPiecesPerBox <= 0) {
      errors.tilesPiecesPerBox = 'Pieces Per Box must be greater than 0';
    }
    if (!form.tilesSqrFtPerBox || form.tilesSqrFtPerBox <= 0) {
      errors.tilesSqrFtPerBox = 'Sqr Ft Per Box must be greater than 0';
    }
    if (!form.tilesImage) errors.tilesImage = 'Tiles Image is required';

    const hasInvalidStock = form.stock.some(item => 
      !item.location ||
      item.freshStockBox < 0 || item.freshStockPieces < 0 ||
      item.damagedStockBox < 0 || item.damagedStockPieces < 0 ||
      item.damagedStockBox > item.freshStockBox || item.damagedStockPieces > item.freshStockPieces
    );

    if (hasInvalidStock) {
      return;
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!selectedBrand?.value) {
      Swal.fire('Error', 'Please select a brand', 'error');
      return;
    }

    if (!form.tilesImage) {
      Swal.fire('Error', 'Please select an image for the tile', 'error');
      return;
    }

    // Create stock array from locations
    const stockData = form.stock.map(item => ({
      location: item.location,
      freshStockBox: parseInt(item.freshStockBox) || 0,
      freshStockPieces: parseInt(item.freshStockPieces) || 0,
      damagedStockBox: parseInt(item.damagedStockBox) || 0,
      damagedStockPieces: parseInt(item.damagedStockPieces) || 0
    }));

    Swal.fire({
      title: 'Creating Tile',
      html: 'Please wait...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const formData = new FormData();
    formData.append('tilesName', form.tilesName);
    formData.append('tilesMM', form.tilesMM);
    formData.append('tilesInches', form.tilesInches);
    formData.append('tilesCategory', form.tilesCategory);
    formData.append('tilesType', form.tilesType);
    formData.append('tilesFinishType', form.tilesFinishType);
    formData.append('tilesPiecesPerBox', form.tilesPiecesPerBox);
    formData.append('tilesSqrFtPerBox', form.tilesSqrFtPerBox);
    formData.append('brand', selectedBrand.value);
    formData.append('stock', JSON.stringify(stockData));
    formData.append('createdBy', user.user._id);

    if (form.tilesImage) {
      formData.append('tilesImage', form.tilesImage);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/tiles`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Tile created successfully',
          timer: 1500
        });
        
        // Delete draft after successful creation
        if (draftManager.currentDraftId) {
          try {
            await draftManager.deleteDraft(draftManager.currentDraftId);
            console.log('Draft deleted after tile creation');
          } catch (error) {
            console.error('Error deleting draft:', error);
          }
        }
        
        // Reset form with initial stock array
        const initialStock = locations.map(location => ({
          location: location._id,
          freshStockBox: 0,
          freshStockPieces: 0,
          damagedStockBox: 0,
          damagedStockPieces: 0
        }));

        setForm({
          tilesName: '',
          partNumber: '',
          displayName: '',
          tilesMM: '',
          tilesInches: '',
          tilesCategory: '',
          tilesType: '',
          tilesFinishType: '',
          tilesPiecesPerBox: '',
          tilesSqrFtPerBox: '',
          tilesImage: null,
          stock: initialStock
        });
        setSelectedBrand(null);
        setImagePreview(null);
        // fetchTiles();
      }
    } catch (error) {
      console.error('Error creating tile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'Failed to create tile'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire('Error', 'Please select a valid image file (JPG, JPEG, PNG)', 'error');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire('Error', 'Image size should be less than 5MB', 'error');
        return;
      }

      setForm({ ...form, tilesImage: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, tilesImage: null });
    setImagePreview(null);
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      handleImageChange({ target: { files: [file] } });
    }
  };

  const ImagePreviewModal = () => (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
      showImageModal ? 'visible bg-black bg-opacity-60 backdrop-blur-sm' : 'invisible'
    }`}>
      <div className={`rounded-2xl p-6 max-w-4xl max-h-[90vh] w-full mx-4 shadow-2xl transform transition-all duration-300 ${
        showImageModal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-2xl font-bold flex items-center gap-2 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <FaImage className="text-blue-500" />
            Image Preview
          </h3>
          <button
            onClick={() => setShowImageModal(false)}
            className={`p-2 rounded-full transition-colors ${
              darkMode 
                ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FaTimes size={24} />
          </button>
        </div>
        <div className="flex justify-center rounded-xl overflow-hidden">
          <img
            src={imagePreview}
            alt="Tile Preview"
            className="max-h-[70vh] object-contain"
          />
        </div>
      </div>
    </div>
  );

  // Helper function for Select styles
  const getSelectStyles = (isDark) => ({
    control: (base, state) => ({
      ...base,
      minHeight: '48px',
      border: state.isFocused 
        ? `2px solid ${isDark ? '#3B82F6' : '#3B82F6'}` 
        : `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
      borderRadius: '0.75rem',
      backgroundColor: isDark ? '#374151' : 'white',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: isDark ? '#6B7280' : '#9CA3AF'
      },
      transition: 'all 0.2s'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : 'white',
      borderRadius: '0.75rem',
      border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3B82F6' 
        : state.isFocused 
        ? (isDark ? '#4B5563' : '#EFF6FF')
        : (isDark ? '#374151' : 'white'),
      color: state.isSelected ? 'white' : (isDark ? '#F3F4F6' : '#374151'),
      '&:hover': {
        backgroundColor: state.isSelected ? '#3B82F6' : (isDark ? '#4B5563' : '#EFF6FF')
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280'
    })
  });

  return (
    <div className={`min-h-screen py-8 transition-colors duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50'
    }`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Modern Header */}
        <div className={`shadow-2xl rounded-2xl mb-8 border-l-4 border-blue-500 ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 mb-2">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    darkMode ? 'bg-blue-500/20' : 'bg-blue-50'
                  }`}>
                    <FaCube className={`text-2xl sm:text-3xl ${
                      darkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      Create New Tile
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`w-2 h-2 rounded-full ${
                        darkMode ? 'bg-green-400' : 'bg-green-500'
                      } animate-pulse`}></div>
                      <p className={`text-xs sm:text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Ready to add inventory
                      </p>
                    </div>
                  </div>
                </div>
                <p className={`mt-2 text-xs sm:text-sm hidden sm:block ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Add a new tile to your inventory with all the necessary details
                </p>
              </div>
              <div className="flex items-center">
                <div className={`px-3 sm:px-4 py-2 rounded-xl flex items-center gap-2 ${
                  darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-700'
                }`}>
                  <FaTags className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium text-xs sm:text-sm">Tile Master</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className={`shadow-2xl rounded-2xl overflow-hidden ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information Section */}
            <div className={`px-4 sm:px-6 py-4 sm:py-6 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-4 sm:mb-6 flex items-center gap-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-blue-500/20' : 'bg-blue-50'
                }`}>
                  <FaBox className={`text-base sm:text-lg ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Tile Name */}
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Tile Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.tilesName}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesName: e.target.value }))}
                    className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-sm border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                    placeholder="Enter tile name"
                    required
                  />
                </div>

                {/* Part Number */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Part Number
                    <span className={`text-xs ml-1 ${
                      darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>(Unique)</span>
                  </label>
                  <input
                    type="text"
                    value={form.partNumber || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, partNumber: e.target.value }))}
                    className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-sm border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                    placeholder="Enter unique part number"
                  />
                </div>

                {/* Display Name */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Display Name
                    <span className={`text-xs ml-1 ${
                      darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>(For dealers)</span>
                  </label>
                  <input
                    type="text"
                    value={form.displayName || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, displayName: e.target.value }))}
                    className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-sm border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                    placeholder="Enter display/design name"
                  />
                </div>

                {/* Brand */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Brand <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedBrand}
                    onChange={setSelectedBrand}
                    options={brands.map(brand => ({
                      value: brand._id,
                      label: brand.name
                    }))}
                    placeholder="Select brand"
                    className="basic-single"
                    classNamePrefix="select"
                    styles={getSelectStyles(darkMode)}
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.tilesCategory}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesCategory: e.target.value }))}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                    required
                  >
                    <option value="">Select category</option>
                    {parameters.category.map(param => (
                      <option key={param._id} value={param._id}>
                        {param.parameterName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.tilesType}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesType: e.target.value }))}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                    required
                  >
                    <option value="">Select type</option>
                    {parameters.type.map(param => (
                      <option key={param._id} value={param._id}>
                        {param.parameterName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Finish Type */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Finish Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.tilesFinishType}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesFinishType: e.target.value }))}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                    required
                  >
                    <option value="">Select finish type</option>
                    {parameters.finish.map(param => (
                      <option key={param._id} value={param._id}>
                        {param.parameterName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Size & Specifications Section */}
            <div className={`px-6 py-6 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 flex items-center gap-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-green-500/20' : 'bg-green-50'
                }`}>
                  <FaRuler className={`text-lg ${
                    darkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                </div>
                Size & Specifications
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Size MM */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Size (MM) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.tilesMM}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesMM: e.target.value }))}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                    required
                  >
                    <option value="">Select size (MM)</option>
                    {parameters.mm.map(param => (
                      <option key={param._id} value={param._id}>
                        {param.parameterName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Size Inches */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Size (Inches) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.tilesInches}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesInches: e.target.value }))}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                    required
                  >
                    <option value="">Select size (Inches)</option>
                    {parameters.inches.map(param => (
                      <option key={param._id} value={param._id}>
                        {param.parameterName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Pieces Per Box */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Pieces Per Box <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={form.tilesPiecesPerBox}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesPiecesPerBox: e.target.value }))}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                    placeholder="0"
                    min="1"
                    required
                  />
                </div>

                {/* Sqr Ft Per Box */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Sqr Ft Per Box <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.tilesSqrFtPerBox}
                    onChange={(e) => setForm(prev => ({ ...prev, tilesSqrFtPerBox: e.target.value }))}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                    placeholder="0.00"
                    min="0.01"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Image Section */}
            <div className={`px-6 py-6 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 flex items-center gap-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-purple-500/20' : 'bg-purple-50'
                }`}>
                  <FaImage className={`text-lg ${
                    darkMode ? 'text-purple-400' : 'text-purple-600'
                  }`} />
                </div>
                Tile Image
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Upload Image <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`flex items-center px-6 py-3 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 hover:bg-gray-650 hover:border-gray-500' 
                        : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                    } focus-within:ring-2 focus-within:ring-blue-500`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <label className="w-full cursor-pointer flex items-center">
                      <FaUpload className={`text-xl mr-3 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                      <span className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {form.tilesImage ? form.tilesImage.name : 'Choose file or drag and drop here'}
                      </span>
                      <input
                        type="file"
                        onChange={handleImageChange}
                        accept="image/png,image/jpeg,image/jpg"
                        className="hidden"
                        required
                      />
                    </label>
                  </div>
                    
                  {imagePreview && (
                    <div className="flex items-center space-x-2 mt-4">
                      <button
                        type="button"
                        onClick={() => setShowImageModal(true)}
                        className={`flex items-center px-4 py-2 text-sm rounded-xl transition-all ${
                          darkMode 
                            ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                        }`}
                      >
                        <FaEye className="mr-2" />
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className={`flex items-center px-4 py-2 text-sm rounded-xl transition-all ${
                          darkMode 
                            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                            : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        <FaTrash className="mr-2" />
                        Remove
                      </button>
                    </div>
                  )}
                  <p className={`mt-2 text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    PNG, JPG, JPEG up to 5MB
                  </p>
                </div>

                {imagePreview && (
                  <div className={`border rounded-xl p-4 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded-lg border border-gray-300 shadow-md"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Stock Information Section */}
            <div className={`px-4 sm:px-6 py-6 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                  darkMode ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20' : 'bg-gradient-to-br from-orange-50 to-orange-100'
                }`}>
                  <FaBox className={`text-xl ${
                    darkMode ? 'text-orange-400' : 'text-orange-600'
                  }`} />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Initial Stock Information
                  </h3>
                  <p className={`text-xs ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Set opening stock levels for each location
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                {form.stock.map((stockItem, index) => {
                  const location = locations.find(loc => loc._id === stockItem.location);
                  return (
                    <div key={stockItem.location} className={`rounded-xl border-2 p-4 transition-all duration-300 ${
                      darkMode 
                        ? 'bg-gradient-to-br from-gray-800/40 to-gray-800/20 border-gray-700/50 hover:border-gray-600 hover:shadow-xl' 
                        : 'bg-gradient-to-br from-white to-gray-50/50 border-gray-200 hover:border-blue-300 hover:shadow-lg'
                    }`}>
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          darkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                        }`}>
                          <FaBox className={`text-sm ${
                            darkMode ? 'text-blue-400' : 'text-blue-600'
                          }`} />
                        </div>
                        <span className={`text-sm font-bold tracking-wide uppercase ${
                          darkMode ? 'text-blue-300' : 'text-blue-900'
                        }`}>{location?.name || 'Unknown Location'}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Fresh Stock - Box */}
                        <div className="space-y-1.5">
                          <label className={`flex items-center gap-1.5 text-xs font-semibold ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              darkMode ? 'bg-green-400' : 'bg-green-500'
                            }`}></div>
                            Fresh Stock (Box)
                          </label>
                          <input
                            type="number"
                            value={stockItem.freshStockBox}
                            onChange={(e) => {
                              const updatedStock = [...form.stock];
                              updatedStock[index].freshStockBox = parseInt(e.target.value) || 0;
                              setForm(prev => ({ ...prev, stock: updatedStock }));
                            }}
                            className={`block w-full px-3 py-2.5 text-sm font-medium border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:bg-gray-650' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-blue-50/30'
                            }`}
                            min="0"
                            placeholder="0"
                          />
                        </div>
                        
                        {/* Fresh Stock - Pieces */}
                        <div className="space-y-1.5">
                          <label className={`flex items-center gap-1.5 text-xs font-semibold ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              darkMode ? 'bg-green-400' : 'bg-green-500'
                            }`}></div>
                            Fresh Stock (Pcs)
                          </label>
                          <input
                            type="number"
                            value={stockItem.freshStockPieces}
                            onChange={(e) => {
                              const updatedStock = [...form.stock];
                              updatedStock[index].freshStockPieces = parseInt(e.target.value) || 0;
                              setForm(prev => ({ ...prev, stock: updatedStock }));
                            }}
                            className={`block w-full px-3 py-2.5 text-sm font-medium border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:bg-gray-650' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-blue-50/30'
                            }`}
                            min="0"
                            placeholder="0"
                          />
                        </div>
                        
                        {/* Damaged Stock - Box */}
                        <div className="space-y-1.5">
                          <label className={`flex items-center gap-1.5 text-xs font-semibold ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              darkMode ? 'bg-red-400' : 'bg-red-500'
                            }`}></div>
                            Damaged Stock (Box)
                          </label>
                          <input
                            type="number"
                            value={stockItem.damagedStockBox}
                            onChange={(e) => {
                              const updatedStock = [...form.stock];
                              updatedStock[index].damagedStockBox = parseInt(e.target.value) || 0;
                              setForm(prev => ({ ...prev, stock: updatedStock }));
                            }}
                            className={`block w-full px-3 py-2.5 text-sm font-medium border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:bg-gray-650' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-blue-50/30'
                            }`}
                            min="0"
                            placeholder="0"
                          />
                        </div>
                        
                        {/* Damaged Stock - Pieces */}
                        <div className="space-y-1.5">
                          <label className={`flex items-center gap-1.5 text-xs font-semibold ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              darkMode ? 'bg-red-400' : 'bg-red-500'
                            }`}></div>
                            Damaged Stock (Pcs)
                          </label>
                          <input
                            type="number"
                            value={stockItem.damagedStockPieces}
                            onChange={(e) => {
                              const updatedStock = [...form.stock];
                              updatedStock[index].damagedStockPieces = parseInt(e.target.value) || 0;
                              setForm(prev => ({ ...prev, stock: updatedStock }));
                            }}
                            className={`block w-full px-3 py-2.5 text-sm font-medium border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:bg-gray-650' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-blue-50/30'
                            }`}
                            min="0"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
            </div>

            {/* Form Actions */}
            <div className={`px-4 sm:px-6 py-4 sm:py-6 flex flex-col sm:flex-row items-center justify-end gap-3 sm:gap-4 ${
              darkMode ? 'bg-gray-750' : 'bg-gray-50'
            }`}>
              <button
                type="button"
                onClick={() => {
                  setForm({
                    tilesName: '',
                    partNumber: '',
                    displayName: '',
                    tilesMM: '',
                    tilesInches: '',
                    tilesCategory: '',
                    tilesType: '',
                    tilesFinishType: '',
                    tilesPiecesPerBox: '',
                    tilesSqrFtPerBox: '',
                    tilesImage: null,
                    stock: locations.map(location => ({
                      location: location._id,
                      freshStockBox: 0,
                      freshStockPieces: 0,
                      damagedStockBox: 0,
                      damagedStockPieces: 0
                    }))
                  });
                  setSelectedBrand(null);
                  setImagePreview(null);
                }}
                className={`w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 border rounded-xl font-medium transition-all text-sm sm:text-base ${
                  darkMode 
                    ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                } focus:ring-2 focus:ring-gray-500`}
              >
                Reset Form
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Tile...
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="mr-2 text-sm sm:text-base" />
                    Create Tile
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Image Preview Modal */}
      <ImagePreviewModal />

      {/* Auto-save Notification */}
      {(draftManager.autoSaving || showNotification) && (
        <div
          className={`fixed bottom-6 right-6 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 flex items-center gap-4 max-w-sm border transition-all duration-300 z-50 ${
              draftManager.autoSaving 
                ? 'border-blue-300 dark:border-blue-700' 
                : 'border-green-300 dark:border-green-700'
            }`}
            onClick={() => !draftManager.autoSaving && setShowNotification(false)}
            title={!draftManager.autoSaving ? 'Click to dismiss' : ''}
            style={{ cursor: draftManager.autoSaving ? 'default' : 'pointer' }}
          >
              <div className={`text-2xl ${draftManager.autoSaving ? 'animate-spin' : ''}`}>
                {draftManager.autoSaving ? '🔄' : '✅'}
              </div>
              <div className="flex-1">
                <p className={`font-semibold text-sm ${
                  draftManager.autoSaving 
                    ? 'text-blue-700 dark:text-blue-400' 
                    : 'text-green-700 dark:text-green-400'
                }`}>
                  {draftManager.autoSaving ? 'Auto-saving...' : notificationMessage}
                </p>
                {draftManager.lastSaved && !draftManager.autoSaving && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {draftManager.lastSaved.toLocaleTimeString('en-IN')}
                  </p>
                )}
              </div>
          </div>
      )}
    </div>
  );
}
